import { makeWidgetProps, BucketFormikForm, BucketFormikInput } from 'Src/types/widget-helpers';
import React, { Fragment, useState } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { Formik } from 'formik';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { t } from 'Src/i18n';
import { Button } from 'Components/frameworks/button/Button';
import { FormFeedback } from 'Components/frameworks/form-feedback/FormFeedback';
import { Icon } from 'Components/frameworks/icon/Icon';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { TextLink } from 'Components/frameworks/text-link/TextLink';
import { Title } from 'Components/frameworks/title/Title';

const FormatedClause = ({ clause }) => {
  const clauseParts = clause.split(':');

  if (clauseParts && clauseParts.length === 2) {
    return (
      <p>
        <span className="font-bold">{clauseParts[0]} :</span>
        {clauseParts[1]}
      </p>
    );
  }

  return <span>{clause}</span>;
};

const FormatedPriceWithLabel = ({ price }) => {
  const priceWithLabel = t('booking.insurance.unitPerTrip', {
    amount: price?.formattedValue || 0,
  });
  const [formatedPrice, label] = priceWithLabel.split('/');
  return (
    <Fragment>
      <span className="text-20 font-bold">{formatedPrice}</span>/ {label}
    </Fragment>
  );
};

const FormatedInformationAndTerms = ({ attachedDocuments, hasError }) => (
  <Fragment>
    <span
      className={classnames(
        {
          'text-red': hasError,
        },
        {
          'text-black': !hasError,
        },
      )}
    >
      {t('booking.insurance.termsAndCondition')}
    </span>
    :
    {attachedDocuments.map(({ label, url }) => {
      return (
        <TextLink
          className="ps-5"
          color={hasError ? COLORS.RED : COLORS.BLACK}
          key={label}
          label={label}
          url={url}
        />
      );
    })}
  </Fragment>
);

type Props = {
  addedInsurance: string;
  addInsurance: (arg0: UpsellInsurance) => any;
  insurance: UpsellInsurance;
  isCartLocked?: boolean;
  removeInsurance: (arg0: UpsellInsurance) => any;
};
export const NInsurance = ({
  addInsurance,
  addedInsurance,
  insurance = makeWidgetProps<Props['insurance']>({}),
  isCartLocked = false,
  removeInsurance,
}: Props) => {
  const [hasError, setHasError] = useState(false);

  const onSubmit = ({ insuranceTerms }) => {
    if (insuranceTerms) {
      setHasError(false);
      const handler = addedInsurance ? removeInsurance : addInsurance;
      handler(insurance);
    } else {
      setHasError(true);
    }
  };

  const price = insurance.pricingInfo?.totalPrice?.formattedValue;
  const btnLabel = addedInsurance ? t('booking.common.remove') : t('booking.common.add');
  const {
    attachedDocuments = [],
    company = makeWidgetProps<{ label: string; logo: string }>({ label: '', logo: '' }),
    coverageDescriptions = [],
    images = [],
    label = '',
    shortDescription,
  } = insurance.productInfo?.insuranceDetails[0] || {};
  return (
    <SiteContainer className="py-20">
      <div
        className="flex flex-col justify-between gap-20 md:flex-row"
        data-testid="InsuranceLayer"
      >
        <div className="flex flex-col gap-10 md:basis-1/4">
          <Icon className="text-black" name={ICONS.INSURANCE} width="2rem" />
          <Title className="text-sienna" theme="title-brand-n3">
            {insurance.productInfo?.label}
          </Title>
          <p className="text-14">{shortDescription}</p>
          {company && 'logo' in company && company.logo && (
            <div className="flex justify-center md:justify-start">
              <img alt={company.label} src={company.logo} />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-20 pl-0 md:basis-3/4 md:pl-80">
          {!!images.length && (
            <div className="relative isolate w-full overflow-hidden rounded-12">
              <img alt={label} className="w-full" src={images[0]} />
            </div>
          )}
          <div className="flex w-full flex-col gap-20 md:flex-row">
            <ul className="space-y-10">
              {coverageDescriptions.map((clause) => (
                <li className="flex items-baseline gap-10 text-14 leading-tight" key={clause}>
                  <Icon className="shrink-0 pt-5 text-green" name={ICONS.CHECK} width="1em" />
                  <FormatedClause clause={clause} />
                </li>
              ))}
            </ul>
            <div className="mx-auto my--20 flex min-h-120 items-center justify-center rounded-12 bg-saffron p-20">
              <p className="text-center text-16 text-black">
                <FormatedPriceWithLabel price={insurance.pricingInfo?.totalPrice} />
                <span className="block font-bold">{t('booking.insurance.coverageModality')}</span>
              </p>
            </div>
          </div>
          <Title className="justify-center text-sienna" theme="title-sans-n4">
            {t('booking.insurance.europAssistanceContent')}
          </Title>
          {!isCartLocked && (
            <Formik
              initialValues={{
                insuranceTerms: false,
              }}
              onSubmit={onSubmit}
            >
              {(formik) => (
                <BucketFormikForm className="flex flex-col items-center gap-20 md:flex-row">
                  <div className="flex-1">
                    <BucketFormikInput
                      className="inline"
                      dataTestId="insuranceTerms"
                      label={
                        <FormatedInformationAndTerms
                          attachedDocuments={attachedDocuments}
                          hasError={hasError && !formik.values.insuranceTerms}
                        />
                      }
                      name="insuranceTerms"
                      type="checkbox"
                    />
                    {hasError && !formik.values.insuranceTerms && (
                      <FormFeedback isError message={t('booking.insurance.errorMessage3')} />
                    )}
                  </div>
                  <Button data-testid={addedInsurance ? 'remove-btn' : 'add-btn'} type="submit">
                    {price ? `${btnLabel} ${price}` : btnLabel}
                  </Button>
                </BucketFormikForm>
              )}
            </Formik>
          )}
        </div>
      </div>
    </SiteContainer>
  );
};
